import React from 'react';
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { RoleClaimsDialogTourConfig } from './RoleClaimsDialogTourConfig';
import { AgTechButton, AgTechButtonProps, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechViewDialog from '@agtech/web/components/Portals/Dialogs/AgTechViewDialog';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechGridProps, gridItems } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';
import { ApplicationRole, ApplicationRoleClaim } from '@sales/data/operation/roles/OperationRoleData';
import { OperationDataGetRoleClaimsQuery } from '@sales/data/operation/roles/OperationRoleQueries';
import { PermissionDescriptions } from './PermissionDescriptions';

export type RoleClaimsDialogProps = {
    applicationRole: ApplicationRole
}

// Read-only - role definition (creating/editing what a role grants) is still sysadmin-only in
// administration.web. This just lets a tenant admin see what a role they're assigning already grants.
//
// Every claim here has ClaimType "permission" (see SalesProject/ROLES_AND_PERMISSIONS.md), so a raw
// Type/Value grid (as used by administration.web's claim editors) would show a useless constant
// column. Instead this shows the permission key plus a plain-English description, since claim
// values like "sales.manage" aren't self-explanatory to a non-technical user.
const RoleClaimsDialog = (props: RoleClaimsDialogProps) => {
    const claimsQuery = useAgTechQuery({
        query: OperationDataGetRoleClaimsQuery,
        queryContext: { roleId: props.applicationRole.id }
    });

    return (
        <AgTechViewDialog
            structure={{
                header: () => ({
                    header: `View Permissions - ${props.applicationRole.roleName}`,
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={RoleClaimsDialogTourConfig} />
                        }
                    }
                }),
                width: '650px'
            }}
        >
            {() => (
                <AgTechQueryLoadingSurface query={claimsQuery}>
                    {(claims: ApplicationRoleClaim[]) => {
                        const gridProps: AgTechGridProps<ApplicationRoleClaim> = {
                            header: {
                                title: 'Permissions',
                                showCount: false
                            },
                            data: {
                                itemQuery: gridItems(claims)
                            },
                            columns: [
                                {
                                    header: 'Permission',
                                    value: c => c.claimValue,
                                    classes: 'emphasized'
                                },
                                {
                                    header: 'Description',
                                    value: c => PermissionDescriptions[c.claimValue] ?? c.claimValue
                                }
                            ],
                            isRowEntityTheSame: (prev, curr) => prev.id === curr.id
                        };

                        return (
                            <div id='role-claims-grid' className='stretched row-based container'>
                                <AgTechGrid {...gridProps} />
                            </div>
                        );
                    }}
                </AgTechQueryLoadingSurface>
            )}
        </AgTechViewDialog>
    )
};

RoleClaimsDialog.ButtonProps = (props: RoleClaimsDialogProps): AgTechButtonProps => ({
    text: 'View Permissions',
    icon: 'fas fa-key',
    action: (e: AgTechClickEventHandler) => e.dialogs.openDialog({
        dialog: () => <RoleClaimsDialog {...props} />
    })
});

RoleClaimsDialog.Button = (props: RoleClaimsDialogProps & AgTechButtonProps) => (
    <AgTechButton {...RoleClaimsDialog.ButtonProps(props)} {...props} />
);

export default RoleClaimsDialog;
